/**
 * @deprecated This screen appears unused. Autopay management is handled by MobileAutopaySection
 * within MobileAccountScreen, using the redesign AutopayCard components.
 */
import React, { useState, useMemo } from 'react';
import { View, Text, ScrollView, RefreshControl } from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import { useAppSelector } from '@/store';
import { selectLeaseIds, selectAllLeaseIds, linkedAcctsConnections, linkedAcctsConnectionsLoading } from '@/store/selectors';
import { useGetAutoPayStatusQuery, useCancelAutoPayMutation } from '@/store/api';
import { LeaseAutopayCard } from '@/components/residents/autopay/LeaseAutopayCard';
import { PaymentIcons } from '@/components/residents/payments/PaymentIcons';
import { MobileScreenHeader } from '@/components/residents/payments/MobileScreenHeader';
import { mobileScreenStyles } from '@/styles/mobileScreenStyles';

export const MobileAutopayManagementScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const { semantic } = useThemeColors();
  const [refreshNonce, setRefreshNonce] = useState(0);
  
  const ownLeaseIds = useAppSelector(selectLeaseIds);
  const connectionLeaseIds = useAppSelector(selectAllLeaseIds);
  const connections = useAppSelector(linkedAcctsConnections);
  const connectionsFetchLoading = useAppSelector(linkedAcctsConnectionsLoading);

  const selfList = ownLeaseIds;
  const connList = useMemo(() => 
    connectionLeaseIds.filter((id) => !ownLeaseIds.includes(id)), 
    [connectionLeaseIds, ownLeaseIds]
  );

  // Build a map of leaseId -> connection user info (email/name) if available
  const connectionUserByLeaseId = useMemo(() => {
    const map = new Map<number, any>();
    (connections || []).forEach((conn: any) => {
      (conn?.leaseIds || []).forEach((id: number) => map.set(id, conn.user));
    });
    return map;
  }, [connections]);

  // Autopay status list (all leases)
  const { refetch: autoPayStatusRefetch, isFetching } = useGetAutoPayStatusQuery();

  const [cancelAutopay] = useCancelAutoPayMutation();

  const handleManageAutopay = (leaseId: number) => {
    navigation.navigate('AutopaySetup', { leaseId });
  };

  const handleRemoveAutopay = async (autopayId: number) => {
    await cancelAutopay(autopayId).unwrap();
    handleRefresh();
  };

  const handleRefresh = () => {
    autoPayStatusRefetch();
    setRefreshNonce((n) => n + 1);
  };

  // Refresh autopay status when screen regains focus
  useFocusEffect(
    React.useCallback(() => {
      handleRefresh();
      return () => {};
    }, [])
  );

  const hasNoLeases = selfList.length === 0 && connList.length === 0;

  const renderEmptyState = () => (
    <View style={mobileScreenStyles.emptyState}>
      <View style={mobileScreenStyles.emptyIconContainer}>
        <PaymentIcons.Calendar />
      </View>
      <Text style={mobileScreenStyles.emptyTitle}>No Leases Available</Text>
      <Text style={mobileScreenStyles.emptyMessage}>
        You don't have any active leases to set up autopay
      </Text>
    </View>
  );

  return (
    <View style={mobileScreenStyles.container}>
      <MobileScreenHeader title="Autopay" />

      <ScrollView 
        style={mobileScreenStyles.content}
        contentContainerStyle={mobileScreenStyles.contentContainer}
        refreshControl={
          <RefreshControl
            refreshing={isFetching}
            onRefresh={handleRefresh}
            tintColor={semantic.interactive.primary}
          />
        }
      >
        {hasNoLeases && !connectionsFetchLoading ? (
          renderEmptyState()
        ) : (
          <>
            <Text style={mobileScreenStyles.sectionDescription}>
              View, add, or remove autopay for your leases
            </Text>

            {/* My Leases */}
            {selfList.length > 0 && (
              <>
                <Text style={mobileScreenStyles.sectionTitle}>My Leases</Text>
                {selfList.map((leaseId) => (
                  <View key={`self-${leaseId}`} style={mobileScreenStyles.cardWrapper}>
                    <LeaseAutopayCard
                      leaseId={leaseId}
                      user={null}
                      onManage={() => handleManageAutopay(leaseId)}
                      onRemove={handleRemoveAutopay}
                      onRefresh={handleRefresh}
                      refreshNonce={refreshNonce}
                    />
                  </View>
                ))}
              </>
            )}

            {/* Connected Leases */}
            {connList.length > 0 && (
              <>
                <Text style={mobileScreenStyles.sectionTitle}>
                  {selfList.length > 0 ? 'Linked Account Leases' : 'Leases'}
                </Text>
                {connList.map((leaseId) => {
                  const connUser = connectionUserByLeaseId.get(leaseId);
                  return (
                    <View key={`conn-${leaseId}`} style={mobileScreenStyles.cardWrapper}>
                      <LeaseAutopayCard
                        leaseId={leaseId}
                        user={connUser}
                        onManage={() => handleManageAutopay(leaseId)}
                        onRemove={handleRemoveAutopay}
                        onRefresh={handleRefresh}
                        refreshNonce={refreshNonce}
                      />
                    </View>
                  );
                })}
              </>
            )}
          </>
        )}
      </ScrollView>
    </View>
  );
};

// Styles removed - using shared mobileScreenStyles

